// articles/ClosestToPinGame.js
// Game guide (Oct 4, 2026), in the Nassau guide's format: a weekly round first, a short
// event note at the end, no printing or event steps. Written from the live code: the CTP
// card in Games Setup (App.js CtpGamePanel — The wager is, Holes Include / Exclude, Also
// on another hole, Must make par, Who has it), the claim bar on the hole screen
// (CtpClaimBar) and the engine (gameEngines/ctp.js). Labels quoted exactly; change them
// here if they change in App.js.
import React from 'react';
import { colors, getGuideBySlug } from '../guidesData';
import { H2, H3, P, UL, LI, GuideCTA, FAQ } from '../articleUI';
import { Step, Btn, Heads, StepTracker, PRINT_CSS } from './eventGuideParts';

const guide = getGuideBySlug('how-to-set-up-closest-to-the-pin');

const FAQS = [
  {
    q: 'Does TTO measure who is closest?',
    a: 'No. Players mark the ball on the green as usual and decide who is closer the way they always have. TTO records who holds each hole: the last player to claim it.',
  },
  {
    q: 'What if nobody hits the green?',
    a: 'Nobody holds the hole, and no money changes hands on it.',
  },
  {
    q: 'Someone claimed a hole by mistake. How do I fix it?',
    a: 'Open 🎮 Games → Closest to the Pin on the day of the round. Under Who has it, pick the right player for that hole, or clear it. After the round is final, change it there and finalize again so the money follows.',
  },
  {
    q: 'Can I play it on a hole that isn\u2019t a par 3?',
    a: 'Yes. Type the hole under Also on another hole. You can also exclude any par 3 you don\u2019t want.',
  },
  {
    q: 'Does it work with Captain\u2019s Choice?',
    a: 'Yes. Closest to the Pin is one of the few games that runs on a round scored one score per team. Any player on the team card can record it, not only the player keeping the card.',
  },
  {
    q: 'Do handicaps matter?',
    a: 'No. Closest to the Pin has no strokes; the closest ball wins.',
  },
];

export default function ClosestToPinGame() {
  return (
    <article>
      <h1
        style={{
          fontFamily: "'DM Serif Display', Georgia, serif",
          fontSize: 38,
          fontWeight: 400,
          color: colors.gray900,
          margin: '0 0 8px',
          lineHeight: 1.15,
        }}
      >
        How to Set Up Closest to the Pin
      </h1>
      <style>{PRINT_CSS}</style>
      <div
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', margin: '0 0 28px' }}
      >
        <p style={{ fontSize: 15, color: colors.gray400, margin: 0 }}>
          {guide.readMinutes} min read &middot; Game guide
        </p>
        <button
          type="button"
          className="guide-no-print"
          onClick={() => window.print()}
          style={{
            background: 'white',
            color: colors.green800,
            border: `1px solid ${colors.gray300}`,
            borderRadius: 8,
            padding: '8px 14px',
            fontSize: 14,
            fontWeight: 700,
            cursor: 'pointer',
          }}
        >
          🖨️ Print this guide
        </button>
      </div>

      <P>
        On each par 3, the player who hits the green closest to the pin wins that hole. TTO
        doesn&rsquo;t measure anything: players mark the ball as usual, and the closest player taps
        to claim the hole in the app. A closer player later takes it over. It runs on its own or
        beside any other game.
      </P>

      <StepTracker
        storageKey="tto_guide_ctp_game_steps"
        steps={['Add Closest to the Pin', 'Who\u2019s in', 'Play & claim', 'Results & settle']}
        note="Steps 1 and 2 happen before the round; 3 and 4 on the day. Tap a step to jump to it."
      />

      <H2>Before you start</H2>
      <UL>
        <LI>
          <strong>Closest to the Pin needs live scoring.</strong> Every player in it needs his scores entered in
          the app as he plays, on his own phone or by the player keeping his group&rsquo;s card.
        </LI>
        <LI>
          <strong>Closest to the Pin isn&rsquo;t in your games list?</strong> Games come with the Plus plan and
          above. On Free or Starter, upgrade under Settings &rarr; Subscription &amp; Billing.
        </LI>
        <LI>
          Decide with your group: which holes, whether a player <strong>must make par</strong>, and
          how the money works (a set prize per hole, or so much a player per hole, or no money).
        </LI>
        <LI>
          <strong>Playing with more than one group?</strong> Bring one marker for each Closest to the
          Pin hole (step 3).
        </LI>
      </UL>

      <H2>Setup, step by step</H2>

      <Step n={1} title="Add Closest to the Pin" when="Before the round">
        <UL>
          <LI>
            Click <Btn>🎮 Games</Btn> on the tee time card and tick <strong>Closest to the Pin</strong>.
          </LI>
          <LI>
            <strong>The wager is:</strong>
            <UL>
              <LI>
                <Btn>A set prize per hole</Btn>: the wager is what each hole&rsquo;s winner collects,
                paid equally by the players in the game.
              </LI>
              <LI>
                <Btn>Each player puts in, per hole</Btn>: the wager is each player&rsquo;s stake per
                hole, and the winner collects it from everyone. $1 a man with 12 players makes a $12
                hole.
              </LI>
            </UL>
            $0 names the winners only and settles no money.
          </LI>
          <LI>
            <strong>Holes:</strong> TTO lists the course&rsquo;s par 3s, each set to{' '}
            <Btn>Include</Btn>. Switch any you don&rsquo;t want to <Btn>Exclude</Btn>. Playing it on
            another hole too? Type it under <strong>Also on another hole</strong>.
          </LI>
          <LI>
            <strong>Must make par</strong> (optional): the question on the hole screen becomes
            &ldquo;On the green, closer, and made par?&rdquo; A closer player who misses his par
            doesn&rsquo;t claim, so the hole stays with the last player who did.
          </LI>
          <LI>
            Click <Btn>Save Games</Btn>.
          </LI>
        </UL>
      </Step>

      <Step n={2} title="Choose who's in" when="After you save, until the first score">
        <UL>
          <LI>
            A new Closest to the Pin game starts with nobody in it. After you save, the game shows a{' '}
            <strong>Player Opt-Ins</strong> list with every player on the tee time.
          </LI>
          <LI>
            Click a player&rsquo;s <strong>— Out</strong> to make it <strong>✓ In</strong>, or the
            reverse.
          </LI>
          <LI>
            Players can also tap <Btn>Opt In</Btn> in their own app, until the first score of the
            round is entered. After that only you can add or remove a player.
          </LI>
          <LI>
            Only players who are in can claim a hole and pay or collect.
          </LI>
        </UL>
      </Step>

      <H2>On the day</H2>

      <Step n={3} title="Play and claim the hole" when="On each Closest to the Pin hole">
        <P>
          TTO records who holds each hole; a <strong>marker on the green</strong> shows every later
          group where the ball to beat is. With more than one group in the game, one marker per
          Closest to the Pin hole is all you need: a tee, a coin with a flag, or a CTP stake.
        </P>
        <H3>The first group to reach the hole</H3>
        <UL>
          <LI>
            The hole screen shows <em>&ldquo;Closest to the Pin: nobody yet&rdquo;</em>.
          </LI>
          <LI>
            The player on the green and closest (who made par, if your game has{' '}
            <strong>Must make par</strong>) taps <Btn>I have it</Btn>, then{' '}
            <Btn>Yes, record it</Btn>. Keeping a partner&rsquo;s card? Tap his name instead.
          </LI>
          <LI>
            <strong>Place the marker beside his ball</strong> before he marks it and putts out, and
            leave it there.
          </LI>
        </UL>
        <H3>Each group after</H3>
        <UL>
          <LI>
            The hole screen shows who has it, for example <em>&ldquo;Bob Smith has it&rdquo;</em>,
            and the marker on the green shows where his ball was.
          </LI>
          <LI>
            A player whose ball finishes <strong>closer than the marker</strong> taps{' '}
            <Btn>I have it</Btn>, then <Btn>Yes, record it</Btn>. That takes the hole over from the
            previous holder.
          </LI>
          <LI>
            <strong>Move the marker to his ball.</strong> It now marks the new ball to beat.
          </LI>
          <LI>
            Nobody closer? Leave the marker where it is and play on; nothing to tap.
          </LI>
        </UL>
        <H3>Until the last group</H3>
        <UL>
          <LI>
            This carries on group after group. The player who holds the hole when the last group has
            played it wins it. The last group picks up the marker.
          </LI>
          <LI>
            Everyone can see the current holders under the leaderboard, in the{' '}
            <strong>📍 Closest to the Pin</strong> list, so a group can check before it reaches the
            hole.
          </LI>
          <LI>
            <strong>Only one group in the game?</strong> No marker needed: the closest player taps{' '}
            <Btn>I have it</Btn> and you&rsquo;re done.
          </LI>
        </UL>
        <H3>A mis-tap or a dispute</H3>
        <UL>
          <LI>
            On the day of the round, <Btn>🎮 Games</Btn> &rarr; Closest to the Pin shows{' '}
            <strong>Who has it</strong>: pick the right player for a hole, or clear it.
          </LI>
        </UL>
      </Step>

      <Step n={4} title="Results and settling up" when="When the last card is in">
        <UL>
          <LI>
            The round finalizes itself once every card is complete. You can also click{' '}
            <Btn>🏁 Finalize Round &amp; Save Settlements</Btn> in <Btn>🎮 Games</Btn>.
          </LI>
          <LI>
            Each hole&rsquo;s holder wins it. A hole nobody claimed pays nothing.
          </LI>
          <LI>
            With money on the game, TTO settles it player to player, every player gets a statement,
            and you can email everyone their results with one click. At $0 the statement shows the
            results only.
          </LI>
          <LI>
            <strong>Something needs fixing after finalize?</strong> Click <Btn>↶ Reopen Round</Btn>{' '}
            in <Btn>🎮 Games</Btn>, make the change, then finalize again so the money follows.
          </LI>
        </UL>
      </Step>

      <H2>Running Closest to the Pin in an event?</H2>
      <P>
        Set the event up with its format guide (📖 Guides &rarr; Setting up Events / Tournaments).
        At the step where you add the main game, tick <strong>Closest to the Pin</strong> beside it and set it
        up as in step 1 above. Most events leave the wager at $0 and pay the winners from the prize table.
      </P>
      <H2>What changes once scoring starts</H2>
      <UL>
        <LI>
          <strong>Players can no longer opt in or out themselves</strong> once the first score of the
          round is entered. You still can, from <Btn>🎮 Games</Btn>.
        </LI>
        <LI>
          <strong>Settings can still change</strong>, but change them only if you must: the results
          and any money follow the new settings.
        </LI>
      </UL>

      <FAQ id="ctp-game" faqs={FAQS} />

      <div className="guide-no-print">
        <GuideCTA />
      </div>
    </article>
  );
}
